import React, { FunctionComponent, useState } from 'react';
import styled from 'styled-components';
import { v1 as uuidv1 } from 'uuid';
import { Esi } from '@empiriecom/module-components/Esi';
import { Modal } from '@empiriecom/module-components/Modal';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import useTracking from '@empiriecom/module-components/hooks/useTracking';
import { Attribute, AttributeValueObject, AttributeBlock } from '@/src/interfaces';
import { PDFIcon as StyledPDFIcon } from '@/src/components/icons';
import { FragmentConfiguration } from '@/config';
import Card from '../Card';
import useUrlParameters from '../hooks/useUrlParameters';

const FeatureWrapper = styled.div<{ isLink?: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  ${({ isLink = false }) => isLink && 'cursor: pointer;'}
  ~ div {
    margin: 0.5rem 0;
  }
  span {
    order: 2;
  }
  a {
    text-decoration: none;
    color: inherit;
  }
`;

const FeatureIcon = styled.img`
  width: 1.5rem;
  height: auto;
  margin-right: 0.5rem;
  order: 1;
`;

const PDFIcon = styled(StyledPDFIcon)`
  margin-right: 0.5rem;
`;

const EsiContainer = styled.div`
  .good-product-panel-headline,
  .sl-cont-headline {
    display: none;
  }
`;

interface ProductAttributesProps {
  /** Object with all attributes from the product. This object will be filtered by the configured values to be rendered. */
  attributes: {
    [key: string]: AttributeBlock;
  };
}

interface AttributeDownloadProps extends Pick<Attribute, 'values'> {
  /** Optional flag if an icon should be rendered.
   * @default true
   */
  renderIcon?: boolean;
}

export const AttributeDownload: FunctionComponent<AttributeDownloadProps> = ({
  values,
  renderIcon = true,
}) => {
  const { link, text } = values[0] as AttributeValueObject;
  return (
    <FeatureWrapper data-testid="feature-wrapper">
      <a href={link} rel="noopener noreferrer" target="_blank">
        {renderIcon && <PDFIcon size="1.5rem" />}
        {text}
      </a>
    </FeatureWrapper>
  );
};

const AttributeIcon: FunctionComponent<{ value: AttributeValueObject; type: string }> = ({
  type,
  value: { link, text, iconUrl },
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const esiUrl = useUrlParameters(link);
  const dispatchGTMEvent = useTracking();
  return (
    <>
      <FeatureWrapper
        data-testid="feature-wrapper"
        key={uuidv1()}
        onClick={() => {
          if (typeof link !== 'undefined') {
            setIsModalOpen(true);
            dispatchGTMEvent({
              event: 'ga_event',
              eventAction: 'viewLayer',
              eventCategory: 'noteLayer',
              eventLabel: 'detailview',
              eventValue: text,
            });
          }
        }}
        isLink={typeof link !== 'undefined'}
      >
        {iconUrl && (
          <FeatureIcon
            data-testid="feature-icon"
            alt={text}
            src={iconUrl.concat('?$format_dv_flagsicons$')}
          />
        )}
        <span data-testid="feature-text">{text}</span>
      </FeatureWrapper>
      {link && (
        <Modal
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          headline={text}
          testId="attribute-modal"
        >
          {type === 'lumberDeclaration' ? (
            <div
              /* Lumber declaration strings can contain html; so we need to set them as innerHtml here */
              /* eslint-disable-next-line react/no-danger */
              dangerouslySetInnerHTML={{
                __html: link,
              }}
            />
          ) : (
            <EsiContainer>
              <Esi src={esiUrl} />
            </EsiContainer>
          )}
        </Modal>
      )}
    </>
  );
};

const AttributeIconGroup: FunctionComponent<Pick<Attribute, 'values' | 'type'>> = ({
  type,
  values,
}) => (
  <>
    {values.map((value: AttributeValueObject) => {
      if (typeof value === 'string' || value === null) {
        return null;
      }
      return <AttributeIcon type={type} value={value} key={value.text} />;
    })}
  </>
);

const ProductAttributesWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 * This component renders the product attributes (also called flags) as cards.<br>
 * The output is differenciated by the `type` property of the passed attribute group
 */
export const ProductAttributes: FunctionComponent<ProductAttributesProps> = ({
  attributes,
}): JSX.Element => {
  if (attributes === undefined) return null;

  const { productAttributes } = useConfig<FragmentConfiguration>();

  return (
    <ProductAttributesWrapper data-testid="product-attributes">
      {productAttributes.map((attrKey) => {
        if (attributes[attrKey] !== undefined) {
          const { headline, values } = attributes[attrKey];
          return (
            <Card contentPadding headline={headline} key={attrKey}>
              {values.map(({ type, values: attrValues }, index) => {
                switch (type) {
                  case 'securityDataSheet':
                  case 'productDataSheet':
                  case 'instruction':
                  case 'manufacturerWarrantyTerms':
                  case 'download':
                    return (
                      <AttributeDownload values={attrValues} key={type.concat(index.toString())} />
                    );
                  default:
                    return (
                      <AttributeIconGroup
                        type={type}
                        values={attrValues}
                        key={type.concat(index.toString())}
                      />
                    );
                }
              })}
            </Card>
          );
        }
        return null;
      })}
    </ProductAttributesWrapper>
  );
};

export default ProductAttributes;
